'use client';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, Box, Button, Divider, SxProps, Theme } from '@mui/material';

import { TabSwitcher } from '@/components/common/TabSwitcher';
// Deep import, not the '@/components' barrel: this module is in the always-rendered
// graph, where a barrel import pulls the whole component tree (charts included).
import { SearchDropdownLoadingSkeleton } from '@/components/skeletons/common/SearchDropdownLoadingSkeleton';
import { useT } from '@/hooks';
import type { EventResponse, SearchProfile } from '@/types';
import { areAllMarketsResolved } from '@/utils';
import { SearchBrowseResults } from './SearchBrowseResults';
import { SearchMarketListItem } from './SearchMarketListItem';
import { SearchNoResults } from './SearchNoResults';
import { SearchProfileResults } from './SearchProfileResults';
import { SearchRecentResults } from './SearchRecentResults';
import { SearchSectionHeader } from './SearchSectionHeader';

import type { BrowseFilter } from '@/constants';

interface SearchDropdownContentProps {
  showResults: boolean;
  isLoading: boolean;
  hasAnyResults: boolean;
  searchQuery: string;
  recentSearches: EventResponse[];
  searchResults: {
    events: EventResponse[];
    profiles: SearchProfile[];
  };
  handleBrowseClick: (filter: BrowseFilter) => void;
  handleResultClick: (result: EventResponse) => void;
  removeFromRecent: (id: string, e: React.MouseEvent) => void;
  handleProfileClick: (profile: SearchProfile) => void;
  handleSeeAllClick: () => void;
  clearAllRecent: () => void;
  activeTab: 'market' | 'profile';
  setActiveTab: (tab: 'market' | 'profile') => void;
  containerStyle?: SxProps<Theme>;
}

export const SearchDropdownContent = ({
  showResults,
  isLoading,
  hasAnyResults,
  searchQuery,
  recentSearches,
  searchResults,
  handleBrowseClick,
  handleResultClick,
  removeFromRecent,
  handleProfileClick,
  handleSeeAllClick,
  clearAllRecent,
  activeTab,
  setActiveTab,
  containerStyle = { p: 1.5 },
}: SearchDropdownContentProps) => {
  const { t } = useT();
  if (!showResults) {
    return (
      <>
        <SearchBrowseResults onFilterClick={handleBrowseClick} />
        {recentSearches.length > 0 && (
          <SearchRecentResults
            searches={recentSearches}
            onResultClick={handleResultClick}
            onRemove={removeFromRecent}
            onClearAll={clearAllRecent}
          />
        )}
      </>
    );
  }

  if (isLoading) {
    return (
      <Box sx={containerStyle}>
        <SearchDropdownLoadingSkeleton />
      </Box>
    );
  }

  if (!hasAnyResults) {
    return (
      <Box sx={containerStyle}>
        <SearchNoResults query={searchQuery} />
      </Box>
    );
  }

  const renderEvents = (event: EventResponse) => (
    <SearchMarketListItem key={event.id} event={event} onClick={handleResultClick} />
  );

  const renderEventResults =
    searchResults?.events
      ?.filter((event) => {
        const isEventResolved = areAllMarketsResolved(event);
        return !isEventResolved;
      })
      .map(renderEvents) || [];

  const tabOptions = [
    { value: 'market', label: t('search.markets') },
    { value: 'profile', label: t('search.profiles') },
  ];

  return (
    <Box sx={containerStyle}>
      <Box>
        {/* Tabs Switcher */}
        <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} options={tabOptions} />

        {activeTab === 'market' && searchResults?.events?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <SearchSectionHeader title={t('search.markets')} icon={SearchIcon} />
            {renderEventResults}
          </Box>
        )}

        {activeTab === 'profile' && searchResults?.profiles?.length > 0 && (
          <SearchProfileResults
            profiles={searchResults.profiles}
            onProfileClick={handleProfileClick}
          />
        )}

        <Divider sx={{ my: 1.5, mx: 1, opacity: 0.5 }} />

        <Button
          fullWidth
          color="primary"
          endIcon={<ArrowForwardIcon />}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 700,
            px: 2,
            py: 1.25,
            borderRadius: '10px',
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
          }}
          onClick={handleSeeAllClick}
        >
          {t('common.seeAllResults', { query: searchQuery })}
        </Button>
      </Box>
    </Box>
  );
};
